import classNames from 'classnames';
import { useFormik } from 'formik';
import { set } from 'lodash';
import capitalize from 'lodash/capitalize';
import get from 'lodash/get';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { findDefaultValue } from '@/modules/common/utils/default-value.ts';
import {
  CompanyDetailsFormik,
  initialValuesCompanyDetails,
  validationSchemaCompanyType,
} from '@/modules/entity/components/company/CompanyDetails.tsx';
import { companyTypes } from '@/modules/entity/static/company-types.ts';
import { countriesGroup1, countryOptions } from '@/modules/entity/static/countries.ts';
import { usaStateOptions } from '@/modules/entity/static/usa-states.ts';
import { ShareholderDetails } from '@/modules/entity/types/EntityItemResponse.ts';
import { OnboardingHolderWrapper } from '@/modules/issuer-dashboard/components/holder-create/OnboardingHolderWrapper.tsx';
import { ChangeShareholderComponentProps } from '@/modules/issuer-dashboard/components/holder-create/roadsTypes.tsx';
import {
  useIssuerGetInvestorEditByIdQuery,
  useUpdateInvestorEditMutation,
} from '@/modules/issuer-dashboard/queries/holder-create.tsx';
import { Button, CustomCheckbox, Select, SelectButtons, SelectOption, TextInputField } from 'ui';
import { useUnsavedChangesStore } from 'ui/hooks';
import { PlusBlueIcon, TrashIcon } from 'ui/icons';
import { UpdateEntityRequest } from 'api';

export interface CompanyDetailsDirector {
  type: 'individual';
  first_name?: string;
  last_name?: string;
  index: string;
}

export interface CompanyDetailsShareholder extends Omit<CompanyDetailsDirector, 'type'> {
  type: 'individual' | 'company';
  company_name?: string;
  same_as?: boolean;
  same_as_index?: string;
}

const stakeholderTypeOptions = [
  { label: 'Individual', value: 'individual' },
  { label: 'Company', value: 'company' },
];

export const CompanyDetailsHolder = ({
  moveForward,
  person_details_key,
  entity_type_prefix,
  index,
  allowSkip,
  title,
  titleHint,
}: ChangeShareholderComponentProps) => {
  const { id: entity_id = 0 } = useParams();
  const { data: dataHolder } = useIssuerGetInvestorEditByIdQuery(
    entity_id ? +entity_id : -1,
    !entity_id || +entity_id === 0 || isNaN(+entity_id),
  );
  const generateInitialValues = (): CompanyDetailsFormik => {
    const entityDetails = dataHolder?.data?.new_data?.entity_data?.company_entity_details;

    if (person_details_key) {
      const personDetails = get(
        dataHolder?.data?.new_data?.entity_data,
        `${entity_type_prefix}.${person_details_key}`,
      )?.find((_: any, indexOfPerson: number) => indexOfPerson === index) as CompanyDetailsFormik;

      return {
        ...initialValuesCompanyDetails,
        ...personDetails,
        country:
          personDetails?.country ||
          dataHolder?.data?.new_data?.entity_data?.country ||
          initialValuesCompanyDetails?.country,
        directors:
          (personDetails?.directors || [])?.map(item => ({
            ...item,
            // type: item.company_name ? 'company' : 'individual',
            // index: item.index || uuidv4(),
          })) || initialValuesCompanyDetails?.directors,
        shareholders:
          (personDetails?.shareholders || [])?.map(item => ({
            ...item,
            // type: item.company_name ? 'company' : 'individual',
            // index: item.index || uuidv4(),
          })) || initialValuesCompanyDetails?.shareholders,
      };
    }

    return {
      ...initialValuesCompanyDetails,
      country:
        dataHolder?.data?.new_data?.entity_data?.country || initialValuesCompanyDetails?.country,
      ...entityDetails,
      directors: entityDetails?.company_name
        ? (entityDetails?.directors || [])?.map(item => ({
            ...item,
            // type: 'individual',
            // index: item.index || uuidv4(),
          }))
        : initialValuesCompanyDetails.directors,
      shareholders: entityDetails?.company_name
        ? (entityDetails?.shareholders || [])?.map(item => ({
            ...item,
            // type: item.company_name ? 'company' : 'individual',
            // index: item.index || uuidv4(),
          }))
        : initialValuesCompanyDetails.shareholders,
    };
  };

  const generatePayload = (values: CompanyDetailsFormik) => {
    if (person_details_key) {
      return set(
        { ...dataHolder?.data?.new_data?.entity_data },
        entity_type_prefix,
        set(
          get(dataHolder?.data?.new_data?.entity_data, entity_type_prefix, {}),
          person_details_key,
          get(
            dataHolder?.data?.new_data?.entity_data,
            `${entity_type_prefix}.${person_details_key}`,
            [],
          ).map((trustee: any, indexOfShareholder: number) =>
            indexOfShareholder === index
              ? {
                  ...trustee,
                  ...values,
                  type: trustee.type || 'company',
                  index: trustee.index || uuidv4(),
                }
              : trustee,
          ),
        ),
      );
    }

    return {
      ...dataHolder?.data?.new_data?.entity_data,
      entity_name: values.company_name,
      company_entity_details: {
        ...dataHolder?.data?.new_data?.entity_data?.company_entity_details,
        ...values,
        registered_office: values?.registered_office || null,
        principal_place_of_business: values?.principal_place_of_business || null,
        directors: (values?.directors || [])?.map(item => ({
          ...item,
          type: 'individual',
          index: item.index || uuidv4(),
        })),
        shareholders: (values?.shareholders || [])?.map(item => ({
          ...item,
          type: item.company_name ? 'company' : 'individual',
          index: item.index || uuidv4(),
        })),
      },
    };
  };

  const formik = useFormik<CompanyDetailsFormik>({
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema: validationSchemaCompanyType,
    initialValues: generateInitialValues(),
    onSubmit: async values => {
      setDirty(false);
      const currentPerson = get(
        dataHolder?.data?.new_data?.entity_data,
        `${entity_type_prefix}.${person_details_key}.${index}`,
        {},
      ) as ShareholderDetails;

      if (formik.dirty || ((!currentPerson?.type || !currentPerson?.index) && person_details_key)) {
        await updateEntity({
          id: dataHolder?.data?.id,
          user_data: dataHolder?.data?.new_data?.user_data,
          dividend_reinvestment: dataHolder?.data?.new_data?.dividend_reinvestment,
          entity_data: generatePayload(values) as UpdateEntityRequest,
        });
      }
      moveForward && moveForward();
    },
  });

  const [dirty, setDirty] = useState(formik.dirty);
  const setUnsavedChanges = useUnsavedChangesStore(state => state.setUnsavedChanges);
  setUnsavedChanges(dirty);
  useEffect(() => {
    setDirty(formik.dirty);
  }, [formik.dirty]);

  const { mutateAsync: updateEntity, isLoading } = useUpdateInvestorEditMutation({
    setErrors: formik.setErrors,
  });

  const handleChangeSelect = (field: string, value: string | boolean) => {
    formik.setFieldValue(field, value);
    formik.setFieldError(field, '');
    if (field === 'country') {
      formik.setFieldValue('entity_number', '');
      formik.setFieldError('entity_number', '');
      formik.setFieldValue('incorporated_state', '');
      formik.setFieldError('incorporated_state', '');
      formik.setFieldValue('nzbn', '');
      formik.setFieldError('nzbn', '');
      formik.setFieldValue('acn', '');
      formik.setFieldError('acn', '');
      formik.setFieldValue('abn', '');
      formik.setFieldError('abn', '');
      formik.setFieldValue('registered_office', '');
      formik.setFieldError('registered_office', '');
      formik.setFieldValue('principal_place_of_business', '');
      formik.setFieldError('principal_place_of_business', '');
    }
  };

  const handleDeleteShareholder = (index: string) => {
    formik.setFieldValue('shareholders', [
      ...(formik.values.shareholders?.filter(item => item.index !== index) ?? []),
    ]);
  };

  const handleAddShareholder = () => {
    formik.setFieldValue('shareholders', [
      ...(formik.values.shareholders ?? []),
      {
        type: 'individual',
        first_name: '',
        last_name: '',
        index: uuidv4(),
        same_as: false,
        same_as_index: '',
      },
    ]);
    formik.setFieldError('shareholders', '');
  };

  const handleDeleteDirector = (index: string) => {
    formik.setFieldValue('directors', [
      ...(formik.values.directors?.filter(item => item.index !== index) ?? []),
    ]);
  };

  const handleAddDirector = () => {
    formik.setFieldValue('directors', [
      ...(formik.values.directors ?? []),
      {
        type: 'individual',
        first_name: '',
        last_name: '',
        index: uuidv4(),
        same_as: false,
        same_as_index: '',
      },
    ]);
    formik.setFieldError('directors', '');
  };

  const getAnotherStakeholders = useCallback(
    (excludeIndex: string[]): SelectOption<string>[] => {
      const trustee: SelectOption<string>[] =
        (formik?.values?.shareholders as CompanyDetailsShareholder[])
          ?.filter(item => !excludeIndex?.includes(item?.index))
          ?.map(item => ({
            value: item.index,
            label: `Shareholder #${
              (formik?.values?.shareholders?.map(item => item.index)?.indexOf(item.index) ?? -1) + 1
            }`,
          })) || [];
      return trustee;
    },
    [formik?.values],
  );

  const getAnotherDirectors = useCallback(
    (excludeIndex: string[]): SelectOption<string>[] => {
      const trustee: SelectOption<string>[] =
        (formik?.values?.directors as CompanyDetailsShareholder[])
          ?.filter(item => !excludeIndex?.includes(item?.index))
          ?.map(item => ({
            value: item.index,
            label: `Director #${
              (formik?.values?.directors?.map(item => item.index)?.indexOf(item.index) ?? -1) + 1
            }`,
          })) || [];
      return trustee;
    },
    [formik?.values],
  );

  useEffect(() => {
    console.log('errors', formik.errors);
  }, [formik.errors]);

  return (
    <OnboardingHolderWrapper
      formik={formik}
      onClickContinue={formik.handleSubmit}
      onClickSkip={
        allowSkip
          ? () => {
              setDirty(false);
              setTimeout(() => {
                moveForward && moveForward();
              });
            }
          : undefined
      }
      isLoading={isLoading}
      title={
        title ||
        (person_details_key
          ? `${capitalize(person_details_key)} Company Details`
          : 'Company Details')
      }
      titleHint={titleHint}
    >
      <>
        <TextInputField label="Full Company Name" name="company_name" />
        {person_details_key && (
          <Select
            isClearable
            label="Country of Formation, Incorporation or Registration"
            options={countryOptions}
            value={formik.values.country}
            onChange={value => handleChangeSelect('country', value || '')}
            error={formik.errors.country}
          />
        )}
        {formik.values.country === 'NZL' && <TextInputField label="nzbn" name="nzbn" />}
        {formik.values.country === 'AUS' && (
          <>
            <div className="w-full flex sm:flex-row flex-col items-start justify-between gap-2">
              <TextInputField label="acn" name="acn" />
              <TextInputField label="abn (optional)" name="abn" />
            </div>
          </>
        )}
        {(formik.values.country === 'AUS' ||
          formik.values.country === 'NZL' ||
          countriesGroup1.includes(formik.values.country)) && (
          <>
            <TextInputField
              label="Registered Office Address"
              name="registered_office"
              placeholder="111/11 Name Street, North Sydney NSW 1111"
            />
            <TextInputField
              label="principal Place Of Business"
              name="principal_place_of_business"
            />
          </>
        )}
        {(formik.values.country === 'USA' || countriesGroup1.includes(formik.values.country)) && (
          <>
            <TextInputField name="entity_number" label="entity number" />
          </>
        )}
        {formik.values.country === 'USA' && (
          <>
            <Select
              isClearable
              label="Company Type (optional)"
              options={companyTypes}
              value={formik.values.private_company_type}
              onChange={value => handleChangeSelect('private_company_type', value || '')}
              error={formik.errors.private_company_type}
            />
            <Select
              isClearable
              label="incorporated state"
              options={usaStateOptions}
              value={formik.values.incorporated_state}
              onChange={value => handleChangeSelect('incorporated_state', value || '')}
              error={formik.errors.incorporated_state}
            />
          </>
        )}
        <>
          <div className="w-full flex flex-col gap-6">
            <div className="flex sm:flex-row flex-col sm:items-center items-start justify-between gap-2 w-full">
              <span className="text-[16px] leading-6 font-[500] font-[Inter]">
                Tell us about your directors
              </span>
              <Button variant="plain" className="flex w-auto gap-1" onClick={handleAddDirector}>
                <img src={PlusBlueIcon} alt="Loading" />
                <span className="text-[14px] leading-[22px]">Add new director</span>
              </Button>
            </div>
            {(formik.values.directors?.length ?? 0) > 0 && (
              <div className="w-full flex flex-col gap-6">
                {formik.values.directors?.map((item, index, all) => {
                  const sameAs = formik.values.shareholders?.find(
                    stakeholder => stakeholder.index === item.same_as_index && item.same_as,
                  );
                  const itemChose = !!formik.values.shareholders?.find(
                    a => a.same_as_index === item.index,
                  );
                  const sameAsDirectorsList = all
                    .filter(d => d.same_as_index && item.index !== d.index)
                    .map(d => d.same_as_index);
                  const sameAsShareholdersList = formik.values.shareholders
                    ?.filter(
                      s =>
                        sameAsDirectorsList.includes(s.index) ||
                        !!s.same_as_index ||
                        s.same_as === true ||
                        s.type !== 'individual',
                    )
                    ?.map(s => s.index);
                  const anotherStakeholders = getAnotherStakeholders([
                    ...(sameAsShareholdersList ?? []),
                    item.index,
                  ]);
                  return (
                    <div key={'directors_' + index} className="w-full flex flex-col gap-4">
                      <div className="w-full flex sm:flex-row flex-col sm:items-center items-start justify-between gap-2">
                        <span className="font-[500] font-[Inter] text-sm leading-[22px]">
                          Director #{index + 1}
                        </span>

                        <div className="flex gap-4 items-center select-none">
                          {anotherStakeholders.length > 0 && !itemChose && (
                            <span className="flex gap-4 items-center">
                              <CustomCheckbox
                                className="w-auto"
                                label={item.same_as ? 'Same as:' : 'Same as'}
                                checked={item.same_as}
                                onChange={e => {
                                  if (!e.target.checked) {
                                    formik.setFieldValue(`directors[${index}].type`, 'individual');
                                    formik.setFieldError(`directors[${index}].type`, 'individual');
                                    formik.setFieldValue(`directors[${index}].same_as_index`, '');
                                  }
                                  handleChangeSelect(
                                    `directors[${index}].same_as`,
                                    e.target.checked,
                                  );
                                }}
                              />
                              <span
                                className={classNames('text-sm leading-5 font-[Inter] w-[160px]', {
                                  hidden: !item.same_as,
                                })}
                              >
                                <Select
                                  options={anotherStakeholders}
                                  value={item.same_as_index}
                                  onChange={value => {
                                    formik.setFieldValue(`directors[${index}].type`, 'individual');
                                    formik.setFieldError(`directors[${index}].type`, '');
                                    formik.setFieldValue(`directors[${index}].first_name`, '');
                                    formik.setFieldValue(`directors[${index}].last_name`, '');
                                    formik.setFieldValue(`directors[${index}].company_name`, '');
                                    formik.setFieldError(`directors[${index}].first_name`, '');
                                    formik.setFieldError(`directors[${index}].last_name`, '');
                                    const indexShareholder = formik.values.shareholders?.findIndex(
                                      item => item.same_as_index === item.index,
                                    );
                                    if ((indexShareholder ?? -1) >= 0) {
                                      handleChangeSelect(
                                        `shareholders[${indexShareholder}].same_as_index`,
                                        '',
                                      );
                                    }
                                    handleChangeSelect(
                                      `directors[${index}].same_as_index`,
                                      value || '',
                                    );
                                  }}
                                  withBorder={false}
                                  showBorderBottom={false}
                                  disabled={!item.same_as}
                                  bgTransparent={true}
                                  isClearable={true}
                                />
                              </span>
                            </span>
                          )}

                          <img
                            onClick={() => {
                              handleDeleteDirector(item.index);
                            }}
                            className="cursor-pointer hover:opacity-80"
                            src={TrashIcon}
                            alt="Loading..."
                          />
                        </div>
                      </div>

                      <div className="w-full flex sm:flex-row flex-col sm:items-center items-start justify-between sm:gap-4 gap-2">
                        <TextInputField
                          name={`directors[${index}].first_name`}
                          value={sameAs?.first_name}
                          label="first name"
                          disabled={!!sameAs}
                        />
                        <TextInputField
                          name={`directors[${index}].last_name`}
                          value={sameAs?.last_name}
                          label="last name"
                          disabled={!!sameAs}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="w-full flex flex-col gap-6">
            <div className="flex sm:flex-row flex-col sm:items-center items-start justify-between gap-2 w-full">
              <span className="text-[16px] leading-6 font-[500] font-[Inter]">
                Tell us about your shareholders
              </span>
              <Button variant="plain" className="flex w-auto gap-1" onClick={handleAddShareholder}>
                <img src={PlusBlueIcon} alt="Loading" />
                <span className="text-[14px] leading-[22px]">Add new shareholder</span>
              </Button>
            </div>
            {(formik.values.shareholders?.length ?? 0) > 0 && (
              <div className="w-full flex flex-col gap-6">
                {formik.values.shareholders?.map((item, index, all) => {
                  const sameAs = formik.values.directors?.find(
                    stakeholder => stakeholder.index === item.same_as_index && item.same_as,
                  );
                  const itemChose = !!formik.values.directors?.find(
                    a => a.same_as_index === item.index,
                  );

                  const sameAsShareholdersList = all
                    .filter(s => s.same_as_index && item.index !== s.index)
                    .map(s => s.same_as_index);
                  const sameAsDirectorList = formik.values.directors
                    ?.filter(
                      d =>
                        sameAsShareholdersList.includes(d.index) ||
                        !!d.same_as_index ||
                        d.same_as === true,
                    )
                    ?.map(d => d.index);
                  const anotherDirectors = getAnotherDirectors([
                    ...(sameAsDirectorList ?? []),
                    item.index,
                  ]);
                  return (
                    <div key={'shareholders_' + index} className="w-full flex flex-col gap-4">
                      <div className="w-full flex sm:flex-row flex-col sm:items-center items-start justify-between gap-2">
                        <span className="font-[500] font-[Inter] text-sm leading-[22px]">
                          Shareholder #{index + 1}
                        </span>
                        <div className="flex gap-4 items-center select-none">
                          {anotherDirectors.length > 0 && !itemChose && (
                            <span className="flex gap-4 items-center">
                              <CustomCheckbox
                                className="w-auto"
                                label={item.same_as ? 'Same as:' : 'Same as'}
                                checked={item.same_as}
                                onChange={e => {
                                  if (!e.target.checked) {
                                    formik.setFieldValue(
                                      `shareholders[${index}].type`,
                                      'individual',
                                    );
                                    formik.setFieldError(
                                      `shareholders[${index}].type`,
                                      'individual',
                                    );
                                    formik.setFieldValue(
                                      `shareholders[${index}].same_as_index`,
                                      '',
                                    );
                                  }
                                  handleChangeSelect(
                                    `shareholders[${index}].same_as`,
                                    e.target.checked,
                                  );
                                }}
                              />
                              <span
                                className={classNames('text-sm leading-5 font-[Inter] w-[160px]', {
                                  hidden: !item.same_as,
                                })}
                              >
                                <Select
                                  options={anotherDirectors}
                                  value={item.same_as_index}
                                  onChange={value => {
                                    formik.setFieldValue(
                                      `shareholders[${index}].type`,
                                      'individual',
                                    );
                                    formik.setFieldError(`shareholders[${index}].type`, '');
                                    formik.setFieldValue(`shareholders[${index}].first_name`, '');
                                    formik.setFieldValue(`shareholders[${index}].last_name`, '');
                                    formik.setFieldValue(`shareholders[${index}].company_name`, '');
                                    formik.setFieldError(`shareholders[${index}].first_name`, '');
                                    formik.setFieldError(`shareholders[${index}].last_name`, '');
                                    formik.setFieldError(`shareholders[${index}].company_name`, '');

                                    const indexDirector = formik.values.directors?.findIndex(
                                      item => item.same_as_index === item.index,
                                    );
                                    if ((indexDirector ?? -1) >= 0) {
                                      handleChangeSelect(
                                        `directors[${indexDirector}].same_as_index`,
                                        '',
                                      );
                                    }
                                    handleChangeSelect(
                                      `shareholders[${index}].same_as_index`,
                                      value || '',
                                    );
                                  }}
                                  withBorder={false}
                                  showBorderBottom={false}
                                  disabled={!item.same_as}
                                  bgTransparent={true}
                                  isClearable={true}
                                />
                              </span>
                            </span>
                          )}

                          <img
                            onClick={() => {
                              formik.setFieldValue(`shareholders[${index}].first_name`, '');
                              formik.setFieldValue(`shareholders[${index}].last_name`, '');
                              formik.setFieldValue(`shareholders[${index}].company_name`, '');
                              formik.setFieldError(`shareholders[${index}].first_name`, '');
                              formik.setFieldError(`shareholders[${index}].last_name`, '');
                              formik.setFieldError(`shareholders[${index}].company_name`, '');
                              handleDeleteShareholder(item.index);
                            }}
                            className="cursor-pointer hover:opacity-80"
                            src={TrashIcon}
                            alt="Loading..."
                          />
                        </div>
                      </div>

                      <SelectButtons<string>
                        label="Do you have individual or a corporate Shareholder?"
                        options={stakeholderTypeOptions}
                        selected={findDefaultValue(
                          stakeholderTypeOptions,
                          sameAs?.type ?? item?.type,
                        )}
                        onChange={value => {
                          if (value === 'individual') {
                            formik.setFieldValue(`shareholders[${index}].company_name`, '');
                            formik.setFieldError(`shareholders[${index}].company_name`, '');
                          }
                          if (value === 'company') {
                            formik.setFieldValue(`shareholders[${index}].first_name`, '');
                            formik.setFieldValue(`shareholders[${index}].last_name`, '');

                            formik.setFieldError(`shareholders[${index}].first_name`, '');
                            formik.setFieldError(`shareholders[${index}].last_name`, '');
                          }
                          const indexDirectorSameAs = formik?.values?.directors
                            ?.map(item => item.same_as_index)
                            ?.indexOf(item.index);
                          if ((indexDirectorSameAs ?? -1) >= 0) {
                            formik.setFieldValue(`directors[${index}].same_as_index`, '');
                            formik.setFieldValue(`directors[${index}].same_as`, false);
                          }
                          handleChangeSelect(`shareholders[${index}].type`, value || '');
                        }}
                        disabled={!!sameAs}
                      />
                      <div className="w-full flex sm:flex-row flex-col sm:items-center items-start justify-between sm:gap-4 gap-2">
                        {((sameAs?.type || item?.type) === 'individual' ||
                          !(sameAs?.type || item?.type)) && (
                          <>
                            <TextInputField
                              label="first name"
                              name={`shareholders[${index}].first_name`}
                              value={sameAs?.first_name}
                              disabled={!!sameAs}
                            />
                            <TextInputField
                              label="last name"
                              name={`shareholders[${index}].last_name`}
                              value={sameAs?.last_name}
                              disabled={!!sameAs}
                            />
                          </>
                        )}
                        {item?.type === 'company' && (
                          <TextInputField
                            label="name"
                            name={`shareholders[${index}].company_name`}
                            disabled={!!sameAs}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      </>
    </OnboardingHolderWrapper>
  );
};
